// DashboardResponsive — dashboard parametrica configurabile per utente
import { useState } from 'react'
import {
  Card, Row, Col, Statistic, Typography, Space, Alert, Tag, Table, Badge,
  Button, Flex, theme,
} from 'antd'
import {
  UserOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  SyncOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
  MessageOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import { useAuth } from '@/contexts/AuthContext'
import { usePermissions } from '@/hooks/usePermissions'
import { usePermission } from '@/hooks/usePermission'
import { useWorkOrders } from '@/hooks/useWorkOrders'
import { useDashboardConfig } from '@/hooks/useDashboardConfig'
import { DashboardConfigDrawer } from '@/components/dashboard/DashboardConfigDrawer'
import { KpiWidget } from '@/components/dashboard/widgets/KpiWidget'
import { TableUltimiWoWidget } from '@/components/dashboard/widgets/TableUltimiWoWidget'
import { TableManutenzioniWidget } from '@/components/dashboard/widgets/TableManutenzioniWidget'
import { TableOrdiniRecentiWidget } from '@/components/dashboard/widgets/TableOrdiniRecentiWidget'
import { TableRdaSenzaRdoWidget } from '@/components/dashboard/widgets/TableRdaSenzaRdoWidget'
import { CalendarMiniWidget } from '@/components/dashboard/widgets/CalendarMiniWidget'
import { KpiWoInLavorazioneWidget } from '@/components/dashboard/widgets/KpiWoInLavorazioneWidget'
import { KpiWoInRitardoWidget } from '@/components/dashboard/widgets/KpiWoInRitardoWidget'
import { KpiWoSinistriWidget } from '@/components/dashboard/widgets/KpiWoSinistriWidget'
import { KpiRdaInApprovazioneWidget } from '@/components/dashboard/widgets/KpiRdaInApprovazioneWidget'
import { KpiRdaRifiutateWidget } from '@/components/dashboard/widgets/KpiRdaRifiutateWidget'
import { KpiRdaDaOrdinareWidget } from '@/components/dashboard/widgets/KpiRdaDaOrdinareWidget'
import { KpiOrdiniInAttesaWidget } from '@/components/dashboard/widgets/KpiOrdiniInAttesaWidget'
import { KpiAutoCortesiaWidget } from '@/components/dashboard/widgets/KpiAutoCortesiaWidget'
import { KpiPrenotazioniCambioWidget } from '@/components/dashboard/widgets/KpiPrenotazioniCambioWidget'
import { TableWoBozzeWidget } from '@/components/dashboard/widgets/TableWoBozzeWidget'
import { TableWoInRitardoWidget } from '@/components/dashboard/widgets/TableWoInRitardoWidget'
import { TableWoSinistriWidget } from '@/components/dashboard/widgets/TableWoSinistriWidget'
import { TableRdaInApprovazioneWidget } from '@/components/dashboard/widgets/TableRdaInApprovazioneWidget'
import { TableRdaRifiutateWidget } from '@/components/dashboard/widgets/TableRdaRifiutateWidget'
import { TableRdaDaOrdinareWidget } from '@/components/dashboard/widgets/TableRdaDaOrdinareWidget'
import { TableOrdiniInAttesaWidget } from '@/components/dashboard/widgets/TableOrdiniInAttesaWidget'
import { TableAutoCortesiaWidget } from '@/components/dashboard/widgets/TableAutoCortesiaWidget'
import { TablePrenotazioniCambioWidget } from '@/components/dashboard/widgets/TablePrenotazioniCambioWidget'
import { KpiRdoConsegnaScadutaWidget } from '@/components/dashboard/widgets/KpiRdoConsegnaScadutaWidget'
import { TableRdoConsegnaScadutaWidget } from '@/components/dashboard/widgets/TableRdoConsegnaScadutaWidget'
import { LINK_CATALOG, WIDGET_CATALOG, checkPermission } from '@/types/dashboard'
import type { WorkOrder } from '@/types'
import {
  SCHEDA_LAVORO_STATUS_COLORS as statoColors,
  SCHEDA_LAVORO_STATUS_LABELS as statoLabels,
} from '@/constants/statuses'

const { Title, Text } = Typography

// ── Tipi ────────────────────────────────────────────────────────────────────

interface StatusCard { nome: string; codice: string; colore: string; count: number | null }

// ── Icone stato ──────────────────────────────────────────────────────────────

const STATO_ICON: Record<string, React.ReactNode> = {
  'in approvazione': <ClockCircleOutlined />,
  'approvata':       <CheckCircleOutlined />,
  'in lavorazione':  <ToolOutlined />,
  'completata':      <SyncOutlined />,
  'sospesa':         <ClockCircleOutlined />,
}

// ── KPI barra stati ──────────────────────────────────────────────────────────

function StatoSchedeKPI({ statusCards, loading }: { statusCards?: StatusCard[]; loading?: boolean }) {
  const navigate = useNavigate()
  const hasStats = statusCards?.some(c => typeof c.count === 'number') ?? false
  if (loading || !hasStats) return null

  return (
    <Row gutter={[16, 16]}>
      {statusCards!.map(c => (
        <Col key={c.nome} xs={12} sm={8} lg={6}>
          <Card size="small" style={{ cursor: 'pointer' }} onClick={() => navigate(`/schede-lavoro?stato=${encodeURIComponent(c.nome)}`)}>
            <Statistic
              title={c.nome}
              value={c.count as number}
              prefix={STATO_ICON[c.nome] ?? <QuestionCircleOutlined />}
              valueStyle={{ color: c.colore }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  )
}

// ── Tabella schede riusabile ─────────────────────────────────────────────────

function SchedeTable({ data, loading, onRowClick }: { data: WorkOrder[]; loading: boolean; onRowClick?: (r: WorkOrder) => void }) {
  const navigate = useNavigate()
  const handleClick = onRowClick ?? ((r: WorkOrder) => navigate(`/schede-lavoro/${r.id}`))

  const columns = [
    {
      title: 'Numero', dataIndex: 'numero_scheda', key: 'numero_scheda', width: 130,
      render: (v: string) => <span style={{ fontWeight: 600, color: '#1677ff' }}><FileTextOutlined style={{ marginRight: 4 }} />{v}</span>,
    },
    {
      title: 'Cliente', dataIndex: 'customer_nome', key: 'customer_nome',
      render: (v: string) => v ?? '—',
    },
    {
      title: 'Veicolo', key: 'veicolo',
      render: (_: any, r: WorkOrder) => (
        <Space size={4}>
          <span>{[r.vehicle_marca, r.vehicle_modello].filter(Boolean).join(' ') || '—'}</span>
          {r.vehicle_targa && <Tag color="geekblue" style={{ fontWeight: 600 }}>{r.vehicle_targa}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Priorità', dataIndex: 'priorita', key: 'priorita', width: 90,
      render: (v: string) =>
        v === 'urgente' ? <Tag color="red">Urgente</Tag> :
        v === 'alta' ? <Tag color="orange">Alta</Tag> : <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'Stato', dataIndex: 'stato', key: 'stato', width: 130,
      render: (v: string) => <Tag color={statoColors[v] ?? 'default'}>{statoLabels[v] ?? v}</Tag>,
    },
    {
      title: 'Appuntamento', dataIndex: 'data_appuntamento', key: 'data_appuntamento', width: 120,
      render: (v: string) => v ? new Date(v).toLocaleDateString('it-IT') : '—',
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="id"
      size="small"
      loading={loading}
      pagination={false}
      scroll={{ x: 700 }}
      locale={{ emptyText: <span style={{ fontSize: 13 }}>Nessuna scheda</span> }}
      onRow={(r) => ({ onClick: () => handleClick(r), style: { cursor: 'pointer' } })}
    />
  )
}

// Codici stato del sistema (tabella work_order_status_types).
// Serve a distinguere "ruolo senza alcun gate" da "ruolo con gate espliciti":
// stessa regola fail-open già usata dalla lista in SchedeLavoro.tsx.
const STATI_CODICI = [
  'bozza', 'accettazione', 'in_analisi', 'in_approvazione', 'approvata',
  'in_lavorazione', 'completata', 'riconsegnata', 'sospesa', 'annullata',
]

// ── Card per singolo stato ───────────────────────────────────────────────────

function SchedeStatoCard({ nome, codice, colore, onRowClick }: {
  nome: string; codice: string; colore: string; onRowClick?: (r: WorkOrder) => void
}) {
  const { hasPermission } = usePermissions()
  const { data, isLoading } = useWorkOrders(1, 100, undefined, nome.toLowerCase())

  // Se il ruolo non ha NESSUN dashboard.stati.*, nessuna restrizione (come SchedeLavoro.tsx).
  // Se ne ha almeno uno, si mostra solo quelli concessi.
  const haGateStati = STATI_CODICI.some(c => hasPermission(`dashboard.stati.${c}`))
  if (haGateStati && !hasPermission(`dashboard.stati.${codice}`)) return null

  const items = data?.items ?? []

  return (
    <Card
      size="small"
      title={
        <Space>
          {STATO_ICON[codice] ?? STATO_ICON[nome] ?? <QuestionCircleOutlined />}
          <span>Schede — {nome}</span>
          <Badge count={items.length} color={colore} showZero />
        </Space>
      }
    >
      <SchedeTable data={items} loading={isLoading} onRowClick={onRowClick} />
    </Card>
  )
}

// ── Widget KPI contatori ─────────────────────────────────────────────────────

function KpiRdaSenzaRdo() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery<{ count: number }>({
    queryKey: ['dashboard', 'manca-rdo'],
    queryFn: () => axiosInstance.get('/purchase-requests/manca-rdo-count').then(r => r.data),
    staleTime: 30000,
  })
  return (
    <KpiWidget
      title="RDA senza RDO"
      value={data?.count}
      loading={isLoading}
      color={data?.count ? '#ff4d4f' : '#52c41a'}
      prefix={<FileTextOutlined />}
      onClick={() => navigate('/rda')}
    />
  )
}

function KpiUtentiPending() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery<any[]>({
    queryKey: ['dashboard', 'utenti-pending'],
    queryFn: () => axiosInstance.get('/users/').then(r => r.data),
    staleTime: 60000,
  })
  const count = (data ?? []).filter((u: any) => u.attivo === false).length
  return (
    <KpiWidget
      title="Utenti in attesa"
      value={count}
      loading={isLoading}
      color={count ? '#faad14' : '#52c41a'}
      prefix={<UserOutlined />}
      onClick={() => navigate('/users')}
    />
  )
}

function KpiManutenzioniScadute() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery<{ overdue: number; upcoming_30_days: number }>({
    queryKey: ['dashboard', 'maintenance-stats'],
    queryFn: () => axiosInstance.get(API_ENDPOINTS.MAINTENANCE_STATS).then(r => r.data),
    staleTime: 60000,
  })
  return (
    <KpiWidget
      title="Manutenzioni scadute"
      value={data?.overdue}
      loading={isLoading}
      color={data?.overdue ? '#ff4d4f' : '#52c41a'}
      prefix={<WarningOutlined />}
      onClick={() => navigate('/maintenance')}
    />
  )
}

function KpiMessaggiNonLetti() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery<{ non_letti: number }>({
    queryKey: ['dashboard', 'messaggi-interni'],
    queryFn: () => axiosInstance.get('/messaggi-interni/alerts').then(r => r.data),
    staleTime: 30000,
  })
  return (
    <KpiWidget
      title="Messaggi non letti"
      value={data?.non_letti}
      loading={isLoading}
      color={data?.non_letti ? '#1677ff' : '#52c41a'}
      prefix={<MessageOutlined />}
      onClick={() => navigate('/comunicazioni')}
    />
  )
}

// ── Renderer widget ──────────────────────────────────────────────────────────

function WidgetRenderer({ id, statusCards }: { id: string; statusCards?: StatusCard[] }) {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()

  const widgetDef = WIDGET_CATALOG.find(w => w.id === id)
  if (widgetDef && !checkPermission(widgetDef.permission, hasPermission)) return null

  switch (id) {
    case 'kpi_stati':
      return <StatoSchedeKPI statusCards={statusCards} />

    case 'kpi_rda_senza_rdo':
      return <KpiRdaSenzaRdo />

    case 'kpi_utenti_pending':
      return <KpiUtentiPending />

    case 'kpi_manutenzioni_scadute':
      return <KpiManutenzioniScadute />

    case 'kpi_messaggi_non_letti':
      return <KpiMessaggiNonLetti />

    case 'table_schede_stato':
      return (
        <>
          {statusCards?.map(card => (
            <SchedeStatoCard
              key={card.codice ?? card.nome}
              nome={card.nome}
              codice={card.codice ?? card.nome.replace(/ /g, '_')}
              colore={card.colore}
              onRowClick={(r: WorkOrder) => navigate(`/schede-lavoro/${r.id}`)}
            />
          ))}
        </>
      )

    case 'table_ultimi_wo':
      return <TableUltimiWoWidget />

    case 'table_manutenzioni':
      return <TableManutenzioniWidget />

    case 'table_ordini_recenti':
      return <TableOrdiniRecentiWidget />

    case 'table_rda_senza_rdo':
      return <TableRdaSenzaRdoWidget />

    // ── KPI nuovi ──────────────────────────────────────────────────────────
    case 'kpi_wo_in_lavorazione':   return <KpiWoInLavorazioneWidget />
    case 'kpi_wo_in_ritardo':       return <KpiWoInRitardoWidget />
    case 'kpi_wo_sinistri':         return <KpiWoSinistriWidget />
    case 'kpi_rda_in_approvazione': return <KpiRdaInApprovazioneWidget />
    case 'kpi_rda_rifiutate':       return <KpiRdaRifiutateWidget />
    case 'kpi_rda_da_ordinare':     return <KpiRdaDaOrdinareWidget />
    case 'kpi_ordini_in_attesa':       return <KpiOrdiniInAttesaWidget />
    case 'kpi_auto_cortesia':          return <KpiAutoCortesiaWidget />
    case 'kpi_prenotazioni_cambio':    return <KpiPrenotazioniCambioWidget />
    case 'kpi_rdo_consegna_scaduta':   return <KpiRdoConsegnaScadutaWidget />

    // ── Tabelle nuove ──────────────────────────────────────────────────────
    case 'table_wo_bozze':             return <TableWoBozzeWidget />
    case 'table_wo_in_ritardo':        return <TableWoInRitardoWidget />
    case 'table_wo_sinistri':          return <TableWoSinistriWidget />
    case 'table_rda_in_approvazione':  return <TableRdaInApprovazioneWidget />
    case 'table_rda_rifiutate':        return <TableRdaRifiutateWidget />
    case 'table_rda_da_ordinare':      return <TableRdaDaOrdinareWidget />
    case 'table_ordini_in_attesa':       return <TableOrdiniInAttesaWidget />
    case 'table_auto_cortesia':          return <TableAutoCortesiaWidget />
    case 'table_prenotazioni_cambio':    return <TablePrenotazioniCambioWidget />
    case 'table_rdo_consegna_scaduta':   return <TableRdoConsegnaScadutaWidget />

    case 'calendar_mini':
      return <CalendarMiniWidget />

    default:
      return null
  }
}

// ── Dashboard principale ─────────────────────────────────────────────────────

export function DashboardResponsive() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const ruolo = user?.ruolo ?? ''
  const { token } = theme.useToken()
  const { hasPermission } = usePermissions()
  const canViewDashboard = usePermission('dashboard.view')

  const { config, saveConfig } = useDashboardConfig()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Status cards — backbone per kpi_stati e table_schede_stato
  const { data: statusCards, error: statusCardsError } = useQuery<StatusCard[]>({
    queryKey: ['dashboard-status-cards'],
    queryFn: async () => {
      const res = await axiosInstance.get<StatusCard[]>(API_ENDPOINTS.DASHBOARD_STATUS_CARDS)
      return res.data
    },
    retry: false,
    staleTime: 0,
    enabled: canViewDashboard,
  })

  const isUnauthorized = (statusCardsError as any)?.response?.status === 403

  if (!canViewDashboard || isUnauthorized) {
    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={2} style={{ marginBottom: 0 }}>
          Dashboard
          {ruolo && <Text type="secondary" style={{ fontSize: 14, marginLeft: 12 }}>({ruolo})</Text>}
        </Title>
        <Alert type="warning" showIcon message="Accesso non autorizzato"
          description="Non sei autorizzato a vedere la dashboard — rivolgiti all'amministratore." />
      </Space>
    )
  }

  // Link rapidi abilitati
  const enabledLinks = LINK_CATALOG
    .filter(l => checkPermission(l.permission, hasPermission))
    .filter(l => config.links.includes(l.id))

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ marginBottom: 0 }}>
          Dashboard
          {ruolo && <Text type="secondary" style={{ fontSize: 14, marginLeft: 12 }}>({ruolo})</Text>}
        </Title>
        <Button
          icon={<SettingOutlined />}
          onClick={() => setDrawerOpen(true)}
        >
          Personalizza
        </Button>
      </div>

      {/* Link rapidi */}
      {enabledLinks.length > 0 && (
        <Card
          size="small"
          title="Azioni rapide"
          style={{ background: token.colorFillQuaternary, borderStyle: 'dashed' }}
        >
          <Flex wrap="wrap" gap={8}>
            {enabledLinks.map(link => (
              <Button key={link.id} onClick={() => navigate(link.route)}>
                {link.label}
              </Button>
            ))}
          </Flex>
        </Card>
      )}

      {/* Widget in ordine */}
      {config.widgets.map(id => (
        <WidgetRenderer key={id} id={id} statusCards={statusCards} />
      ))}

      {/* Drawer configurazione */}
      <DashboardConfigDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        config={config}
        onSave={(newConfig) => {
          saveConfig(newConfig)
          setDrawerOpen(false)
        }}
      />
    </Space>
  )
}

export default DashboardResponsive
